import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const polypectomy: ContentBlock<"procedure"> = {
  id: makeId("proc-polypectomy"),
  kind: "procedure",
  tags: ["her", "testing", "pre-cycle", "clinic-visit"],
  title: "Polypectomy",
  subtitle: "Removal of uterine polyps to improve implantation",
  body: {
    procedureName: "Hysteroscopic Polypectomy",
    category: "diagnostic",
    description:
      "A minimally invasive procedure to remove endometrial polyps (small growths on the uterine lining) via hysteroscopy. Polyps can interfere with embryo implantation and are one of the most correctable causes of implantation failure.",
    howItWorks:
      "A thin hysteroscope is inserted through the cervix. The polyp is visualized on a monitor and removed using specialized grasping instruments, an electrosurgical loop, or a hysteroscopic morcellator. The base of the polyp is treated to prevent recurrence. The tissue is sent to pathology to confirm it is benign.",
    duration: "15-30 minutes",
    costRange: { low: 1500, high: 4000 },
    currency: "USD",
    anesthesia: "sedation",
    recoveryTime: "Same day; return to normal activity in 1-2 days",
    whatToExpect: [
      "Quick outpatient procedure",
      "Mild cramping similar to period cramps for a few hours",
      "Light spotting for 1-3 days",
      "Can typically resume embryo transfer cycle within 1-2 months",
      "Pathology results in 1-2 weeks (almost always benign)",
    ],
    results: [
      {
        label: "Pathology",
        detail:
          "Removed tissue is usually sent to pathology. Results often return in 1-2 weeks and are usually benign.",
      },
      {
        label: "What you learn",
        detail:
          "The report confirms what was removed and checks for uncommon abnormal cells.",
      },
      {
        label: "Next cycle",
        detail:
          "Your doctor will tell you when it is safe to resume treatment. Many people can restart within 1-2 months.",
      },
    ],
    prepInstructions: [
      "Schedule in the first half of the menstrual cycle",
      "No food or drink if sedation is planned",
      "Arrange for a ride home",
    ],
    risks: [
      "Mild cramping and spotting (common, brief)",
      "Infection (very rare)",
      "Uterine perforation (very rare)",
      "Polyp recurrence (10-15% over time)",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Polypectomie hystéroscopique",
      description:
        "Geste mini-invasif d'ablation des polypes endométriaux (petites excroissances de la muqueuse utérine) par hystéroscopie. Les polypes peuvent gêner l'implantation et représentent l'une des causes d'échec d'implantation les plus facilement corrigeables.",
      howItWorks:
        "Un fin hystéroscope est introduit à travers le col. Le polype est visualisé à l'écran puis retiré à l'aide de pinces spécifiques, d'une anse électrochirurgicale ou d'un morcellateur hystéroscopique. La base d'insertion est traitée pour éviter une récidive. Le tissu est envoyé en anatomopathologie pour confirmer son caractère bénin.",
      duration: "15 à 30 minutes",
      recoveryTime:
        "Sortie le jour même ; reprise des activités normales en 1 à 2 jours",
      whatToExpect: [
        "Geste rapide en ambulatoire",
        "Crampes légères de type menstruel pendant quelques heures",
        "Léger spotting pendant 1 à 3 jours",
        "Reprise du cycle de transfert d'embryon généralement possible sous 1 à 2 mois",
        "Résultats anatomopathologiques sous 1 à 2 semaines (presque toujours bénins)",
      ],
      results: [
        {
          label: "Anatomopathologie",
          detail:
            "Le tissu retiré est généralement envoyé en analyse. Les résultats reviennent souvent sous 1 à 2 semaines et sont le plus souvent bénins.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "Le compte rendu confirme ce qui a été retiré et recherche de rares cellules anormales.",
        },
        {
          label: "Cycle suivant",
          detail:
            "Votre médecin vous dira quand reprendre le traitement. Beaucoup de personnes peuvent recommencer sous 1 à 2 mois.",
        },
      ],
      prepInstructions: [
        "À programmer en première moitié de cycle",
        "À jeun si une sédation est prévue",
        "Prévoyez quelqu'un pour vous raccompagner",
      ],
      risks: [
        "Crampes et spotting légers (fréquents, brefs)",
        "Infection (très rare)",
        "Perforation utérine (très rare)",
        "Récidive du polype (10 à 15 % avec le temps)",
      ],
    },
  },
  sources: [
    {
      title:
        "AAGL Practice Report: Practice Guidelines for the Diagnosis and Management of Endometrial Polyps",
      authors: "AAGL",
      journal: "Journal of Minimally Invasive Gynecology",
      year: 2012,
      url: "https://pubmed.ncbi.nlm.nih.gov/22196255/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(polypectomy);
